import { useState } from "react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Calendar as CalendarIcon, Filter, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

interface StockMovementsFiltersProps {
  startDate: Date | undefined;
  endDate: Date | undefined;
  movementType: "entry" | "exit" | "sale" | "adjustment" | "all";
  itemCategory: "ingredient" | "product" | "all";
  onStartDateChange: (date: Date | undefined) => void;
  onEndDateChange: (date: Date | undefined) => void;
  onMovementTypeChange: (type: "entry" | "exit" | "sale" | "adjustment" | "all") => void;
  onItemCategoryChange: (category: "ingredient" | "product" | "all") => void;
  onClearFilters: () => void;
}

export function StockMovementsFilters({
  startDate,
  endDate,
  movementType,
  itemCategory,
  onStartDateChange,
  onEndDateChange,
  onMovementTypeChange,
  onItemCategoryChange,
  onClearFilters,
}: StockMovementsFiltersProps) {
  const hasActiveFilters = startDate || endDate || movementType !== "all" || itemCategory !== "all";

  return (
    <div className="flex flex-wrap items-center gap-3 p-4 bg-muted/30 rounded-lg border">
      <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
        <Filter className="h-4 w-4" />
        Filtros:
      </div>

      {/* Date Range */}
      <div className="flex items-center gap-2">
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className={cn(
                "justify-start text-left font-normal",
                !startDate && "text-muted-foreground"
              )}
            >
              <CalendarIcon className="mr-2 h-4 w-4" />
              {startDate ? format(startDate, "dd/MM/yyyy", { locale: ptBR }) : "Data inicial"}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="single"
              selected={startDate}
              onSelect={onStartDateChange}
              locale={ptBR}
              initialFocus
            />
          </PopoverContent>
        </Popover>

        <span className="text-muted-foreground">até</span>

        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className={cn(
                "justify-start text-left font-normal",
                !endDate && "text-muted-foreground"
              )}
            >
              <CalendarIcon className="mr-2 h-4 w-4" />
              {endDate ? format(endDate, "dd/MM/yyyy", { locale: ptBR }) : "Data final"}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="single"
              selected={endDate}
              onSelect={onEndDateChange}
              locale={ptBR}
              initialFocus
            />
          </PopoverContent>
        </Popover>
      </div>

      {/* Movement Type */}
      <Select value={movementType} onValueChange={(v) => onMovementTypeChange(v as any)}>
        <SelectTrigger className="w-[140px] h-9">
          <SelectValue placeholder="Tipo" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Todos os tipos</SelectItem>
          <SelectItem value="entry">Entrada</SelectItem>
          <SelectItem value="exit">Saída</SelectItem>
          <SelectItem value="sale">Venda</SelectItem>
          <SelectItem value="adjustment">Ajuste</SelectItem>
        </SelectContent>
      </Select>

      {/* Item Category */}
      <Select value={itemCategory} onValueChange={(v) => onItemCategoryChange(v as any)}>
        <SelectTrigger className="w-[140px] h-9">
          <SelectValue placeholder="Categoria" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Todas categorias</SelectItem>
          <SelectItem value="ingredient">Ingredientes</SelectItem>
          <SelectItem value="product">Produtos</SelectItem>
        </SelectContent>
      </Select>

      {/* Clear Filters */}
      {hasActiveFilters && (
        <Button
          variant="ghost"
          size="sm"
          onClick={onClearFilters}
          className="text-muted-foreground hover:text-foreground"
        >
          <X className="h-4 w-4 mr-1" />
          Limpar
        </Button>
      )}
    </div>
  );
}
